// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StyledButton } from "@insite/mobius/Clickable";
import DataTablePresentationPropsDefault from "@insite/mobius/DataTable/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { DataTableCssOverrides } from "@synd/MobiusOverrides/DataTable/DataTable";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { xBaseTheme } from "@synd/XDL/types";
import { color, xScreen } from "@synd/XDL/Utilities";
import { css } from "styled-components";
import AscendingSort from "@synd/XDL/Icons/AscendingSort";
import DescendingSort from "@synd/XDL/Icons/DescendingSort";
import Sort from "@synd/XDL/Icons/Sort";

const stickyMode = () => css`
    ${xScreen("lte", "sm")} {
        position: sticky;
        left: 0;
        z-index: 2;
        background-clip: padding-box;
        background: inherit;
        > ${TypographyStyle} {
            display: block;
            max-width: 140px;
            text-overflow: ellipsis;
            overflow: hidden;
        }
    }
`;

export default {
    defaultProps: {
        ...DataTablePresentationPropsDefault,
        mergeCss: true,
        cssOverrides: {
            header: css`
                background: ${color("common.ui8")};
                padding: 4px 8px;
                border-bottom: 0;
                height: initial;
                line-height: normal;
                box-shadow: 0 -1px 0 0 inset ${color("common.ui4")};
            `,
            head: css`
                background: ${color("common.ui8")};
                th:first-child {
                    ${stickyMode()}
                }
            `,
            cell: css`
                padding: 8px;
                border-bottom: 0;
                height: initial;
                line-height: normal;
                ${StyledButton} > ${TypographyStyle} {
                    font-size: ${fontConfig.smallSingleLine.size}px;
                    line-height: ${fontConfig.smallSingleLine.lineHeight};
                }
                &:first-child {
                    ${stickyMode()}
                }
            `,
            row: css`
                background: ${color("common.white")};
            `,
            evenRow: css`
                background: ${color("common.ui2")};
            `,
        } as DataTableCssOverrides,
        headerTypographyProps: {
            mergeCss: true,
            size: fontConfig.xSmallSingleLine.size,
            weight: fontConfig.weight.semiBold,
            css: css`
                letter-spacing: 0;
                line-height: ${fontConfig.xSmallSingleLine.lineHeight};
            `,
        },
        cellTypographyProps: {
            mergeCss: true,
            size: fontConfig.small.size,
            weight: fontConfig.weight.regular,
            css: css`
                letter-spacing: 0;
                line-height: 19px;
            `,
        },
        sortClickableProps: {
            css: css`
                width: 100%;
                height: 100%;
                &:hover {
                    ${TypographyStyle}, ${IconWrapper} {
                        color: ${color("common.ui4")};
                    }
                }
            `,
        },
        sortIconSources: {
            sortable: Sort,
            ascending: AscendingSort,
            descending: DescendingSort,
        },
        sortIconProps: {
            size: 10,
            color: "common.ui4",
            css: css`
                margin-left: auto;
            `,
        },
        css: css`
            td[class^="DataTableCellBase"] {
                & > ${TypographyStyle}:empty:after {
                    content: "- -";
                }
            }

            div[class^="MobileDataTableCellBase"] {
                & > [class^="ValueTypography"]:empty:after {
                    content: "- -";
                }
            }
        `,
    },
} as xBaseTheme["dataTable"];
